/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Breadcrumbs,
  FormControl,
  Pagination,
  Select,
  Table,
  TableBody,
  TableRow,
  Typography,
  TableSortLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api/index.js";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { MenuHeading } from "../../../components/menu_heading";
import camelize from "../../../utils/camelize.js";
import { Loader } from "../../../components/loader";
import {
  StyledPaginationContainer,
  StyledPerPageContainer,
  StyledTableBodyCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTypography,
} from "../../../components/index_table/styles";
import { useTranslation } from "react-i18next";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import { FilterContainer } from "../../../components/filter_container";
import { selectBaseStyles, viewTypographyStyles } from "../styles";
import { StyledInputLabel } from "../../restaurant/styles.jsx";
import { UpdateButton } from "../../../components/update_button/index.jsx";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import DropdownSearchBar from "../../../components/dorpdown_searchbar/index.jsx";
import { ResetButton } from "../../../components/reset_button/index.jsx";
import { SelectedMenuItem } from '../../../components/selected_menu_item/index.jsx'
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import { buildQueryParams } from "../../../utils/buildQueryParams.js";
import { useNotificationContext } from "../../../contexts/notificationContext.jsx";
import csvLoader from '../../../assets/images/csv2.gif'
import { scrollbarStyles } from "../styles";
import { StyledMenuItem } from "../../../components/styled_menu_item/index.jsx";
import { perPageStyles, StyledTypography as TableDetailText } from '../../reports/homepage/styles.js'
import { hasSingleItem } from "../../../utils/hasSingleItem.js";
import { getSingleItemValue } from "../../../utils/getSingleItemValue.js";
import { Query_Keys } from "../../../lib/queryKeys.js";
import { PerPage } from "../../../constants/perPage";
import { useFillViewportHeight } from "../../../hooks/useFillViewportHeight";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const datePickerStyles = {
  ".MuiInputBase-root": {
    height: "2.4rem",
    backgroundColor: "white",
    fontSize: "14px",
  },
  "& input::placeholder": {
    color: "black",
  },
  width: 160,
};

const ALL_VALUE = 'All'
const defaultFilter = {
  restaurant_group_id: ALL_VALUE,
  dateFilter: ALL_VALUE,
  restaurants: [ALL_VALUE],
  customDateFrom: null,
  customDateTo: null,
}

export const DetailedTransaction = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const storageKey = 'tableWidths-detailedTransaction';
  const [csvLoading, setCsvLoading] = useState(false)
  const showNotification = useNotificationContext()
  const [scrollWidth, setScrollWidth] = useState(0);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const tableRef = useRef(null);
  const fakeScrollRef = useRef(null);
  const tableFooterRef = useRef(null);

  const containerMaxHeight = useFillViewportHeight({
    containerRef: tableRef,
    bottomRef: tableFooterRef,
  });
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);

  const resizingRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });

  const { t } = useTranslation();

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })
    return response?.data?.map(normalizeRestaurantGroup) || []
  }

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id,
      },
    });

    return response?.data?.map(normalizeRestaurant)?.filter((item) => item?.id && item?.name) || [];
  }

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const fetchCrossRestaurantData = async () => {
    const newFormattedFilters = formattedFilters(appliedFilters);
    const response = await api.get("/v1/admin/reports/cross_restaurant_discounts", {
      params: { ...newFormattedFilters },
    })
    const newData = response?.data.map((item) => {
      const { employee_from, ...rest } = item;
      const newData = { "from/To": employee_from, ...rest };
      return newData;
    })
    return newData
  }

  const { data, isFetching } = useQuery({
    queryKey: Query_Keys.cross_restaurant_data({
      filters: appliedFilters,
    }),
    queryFn: fetchCrossRestaurantData,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      setFilters((prev) => ({
        ...prev,
        restaurants: [restaurants[0]?.name],
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  const handleRestaurantGroupChange = (event) => {
    setFilters({ ...filters, restaurant_group_id: event.target.value, restaurants: [ALL_VALUE] });
    setFiltersChanged(true);
  };

  const handleRestaurantGroupClear = () => {
    setFilters({ ...filters, restaurant_group_id: ALL_VALUE, restaurants: [ALL_VALUE] });
    setFiltersChanged(true);
  }

  const handleMultiSelectChange = (event) => {
    const {
      target: { value },
    } = event;

    setFiltersChanged(true);

    if ((value.length === 1 && value.includes(ALL_VALUE)) || value.length === 0) {
      setFilters({ ...filters, restaurants: [ALL_VALUE] });
    } else {
      const nextRestaurants = value.filter((val) => val !== ALL_VALUE);
      setFilters({ ...filters, restaurants: nextRestaurants });
    }
  }

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurants: [ALL_VALUE] })
    setFiltersChanged(true)
  }

  const handleDateRangeClear = () => {
    setFilters({
      ...filters,
      dateFilter: ALL_VALUE,
      customDateFrom: null,
      customDateTo: null,
    });
    setFiltersChanged(true)
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      if (field === "customDateFrom") {
        if (!value) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: value };
    });
    setFiltersChanged(true);
  };

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleUpdate = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setEntriesPerPage(25);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setFilters(defaultFilter)
    setEntriesPerPage(25);
    setCurrentPage(1);
  }

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurants || filters.restaurants.length === 0) {
      return 'All';
    }

    if (filters.restaurants.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters.restaurants.length === 1) {
      return restaurantOptionsCache.find((restaurant) => restaurant?.name === filters.restaurants[0])?.name || filters.restaurants[0] || 'All';
    }

    return `${filters.restaurants.length} Restaurants Selected`;
  }

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup ? restaurantGroup?.value : 'All'
  }

  const handleOrderCsvExport = async () => {
    try {
      setCsvLoading(true);
      const exportRestaurants = (appliedFilters?.restaurants || [ALL_VALUE])
        .filter(Boolean)
        .filter((restaurant) => restaurant !== ALL_VALUE);
      const formattedCsvFilters = {
        ...formattedFilters(appliedFilters),
        ...(exportRestaurants.length ? { restaurants: exportRestaurants } : {}),
        ...(orderBy ? { sort_by: orderBy, sort_order: order } : {}),
      }
      const query = buildQueryParams(formattedCsvFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=cross_restaurant_discounts&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `cross_restaurant_discounts_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const formattedFilters = (sourceFilters = appliedFilters) => {
    const params = {};
    const today = dayjs();

    // Date range handling
    if (sourceFilters.dateFilter !== "Custom Date" && sourceFilters.dateFilter !== ALL_VALUE) {
      switch (sourceFilters.dateFilter) {
        case "Today":
          params.date_range = {
            from: today.format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Last 7 Days":
          params.date_range = {
            from: today.subtract(7, "day").format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Yesterday":
          params.date_range = {
            from: today.subtract(1, "day").format("DD/MM/YYYY"),
            to: today.subtract(1, "day").format("DD/MM/YYYY"),
          };
          break;
        case "This Week":
          params.date_range = {
            from: today.startOf("week").format("DD/MM/YYYY"),
            to: today.endOf("week").format("DD/MM/YYYY"),
          };
          break;
        case "This Month":
          params.date_range = {
            from: today.startOf("month").format("DD/MM/YYYY"),
            to: today.endOf("month").format("DD/MM/YYYY"),
          };
          break;
        case "Last Month":
          params.date_range = {
            from: today
              .subtract(1, "month")
              .startOf("month")
              .format("DD/MM/YYYY"),
            to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
          };
          break;
      }
    } else if (sourceFilters.dateFilter === "Custom Date" && sourceFilters.customDateFrom && sourceFilters.customDateTo) {
      params.date_range = {
        from: dayjs(sourceFilters.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(sourceFilters.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (sourceFilters?.restaurant_group_id !== ALL_VALUE)
      params.restaurant_group_ids = [sourceFilters?.restaurant_group_id];

    return params;
  };

  React.useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label,
        restaurants: [ALL_VALUE],
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  const Days = [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ];

  const filteredData = useMemo(() => {
    const appliedRestaurants = appliedFilters?.restaurants || [ALL_VALUE];

    if (!data) return []

    if (appliedRestaurants.includes(ALL_VALUE)) return data;

    if (appliedRestaurants.length === 0) return []

    return data.filter(item =>
      appliedRestaurants.includes(item["from/To"])
    );
  }, [data, appliedFilters])

  const tableColumns = useMemo(() => {
    const appliedRestaurants = appliedFilters?.restaurants || [ALL_VALUE];

    if (!data) return [];

    if (!appliedRestaurants.includes(ALL_VALUE) && appliedRestaurants.length === 0) return [];

    const originalColumnOrder = data.length > 0 ? Object.keys(data[0]) : [];
    const filteredColumns = originalColumnOrder.filter(col =>
      col === "from/To" || appliedRestaurants.includes(ALL_VALUE) || appliedRestaurants.includes(col)
    );
    return filteredColumns;
  }, [data, appliedFilters]);

  const columns = tableColumns?.map((column) => {
    return {
      key: column,
      label: camelize(column),
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      let newData = {};

      tableColumns.map(
        (item) =>
          (newData[item] =
            record[item] !== null && record[item] !== undefined
              ? item === "from/To"
                ? record[item]
                : `$${Number(record[item]).toFixed(2) ?? '0.00'}`
              : "-")
      );
      return newData;
    });
  };

  let rows = TableRows(filteredData);

  const totalItems = rows?.length;
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const totalPages = Math.ceil(totalItems / entriesPerPage);

  const isDiscountCardsPath =
    /^\/admin\/perq_cards\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/create\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/edit\/\d+\/?$/.test(location.pathname);

    const handleSort = (property) => {
      const isAsc = orderBy === property.key && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property.key);
    };
  
    function descendingComparator(a, b, orderBy) {
      const isNumeric = (string) => /^[+-/$]?\d+(\.\d+)?$/.test(string);
  
      if (isNumeric(a[orderBy]) && isNumeric(b[orderBy])) {
        if (a[orderBy][0] === "$" && b[orderBy][0] === "$") {
          return (
            Number(b[orderBy].substring(1)) - Number(a[orderBy].substring(1))
          );
        }
        return b[orderBy] - a[orderBy];
      }
  
      if (b[orderBy] || '' < a[orderBy] || '') {
        return -1;
      }
      if (b[orderBy] || '' > a[orderBy] || '') {
        return 1;
      }
      return 0;
    }
  
    function getComparator(order, orderBy) {
      return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    }
  
    const visibleRows = useMemo(() => {
      if(rows){
        return [...rows].sort(getComparator(order, orderBy));
      }
    }, [rows, order, orderBy]);

  const normalizeKey = (label) => label?.split(' ').join('_');

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) return JSON.parse(saved);
    return columns.reduce((acc, col) => {
      acc[normalizeKey(col.key)] = 150;
      return acc;
    }, {});
  });

  // Handles horizontal scroll
  const handleMainScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    fakeScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
  };

  const handleFakeScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    tableRef.current.scrollLeft = fakeScrollRef.current.scrollLeft;
  };

  useEffect(() => {
    if (tableRef.current) {
      setScrollWidth(tableRef.current.scrollWidth);
    }
  }, [columnWidths, rows]);

  useEffect(() => {
    const onResize = () => {
      if (tableRef.current) {
        setScrollWidth(tableRef.current.scrollWidth);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Handles column resizing
  const startResizing = (key, e) => {
    e.preventDefault();
    resizingRef.current = { columnKey: normalizeKey(key), startX: e.clientX, startWidth: columnWidths[normalizeKey(key)] };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;
    const newWidth = Math.max(80, startWidth + e.clientX - startX);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  }, []);

  const handleMouseUp = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;

    const finalWidth = Math.max(80, startWidth + e.clientX - startX);

    localStorage.setItem(storageKey, JSON.stringify({ ...columnWidths, [columnKey]: finalWidth }));
    setColumnWidths((prev) => ({ ...prev, [columnKey]: finalWidth }));

    resizingRef.current = { columnKey: null, startX: 0, startWidth: 0 };
    document.body.style.cursor = "";

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [columnWidths, storageKey, handleMouseMove]);

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Perq Reports</StyledLink>
            <StyledLink color={"#515152"}>
              Restaurant Perq
            </StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box 
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box 
              sx={{ 
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <Typography variant="body2" 
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap"
                }}
              >
                View
              </Typography>

              <FormControl 
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  value={hasSingleAvailableRestaurantGroup
                    ? getSingleItemValue(restaurantGroups)?.label
                    : (filters?.restaurant_group_id || ALL_VALUE)}
                  renderValue={() => {
                    if (hasSingleAvailableRestaurantGroup) {
                      return getSingleItemValue(restaurantGroups)?.value || 'All';
                    }
                    return getRestaurantGroupName(filters?.restaurant_group_id);
                  }}
                  label="Restaurant Group"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    width: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  endAdornment= {
                    filters?.restaurant_group_id !== ALL_VALUE && !hasSingleAvailableRestaurantGroup ?(
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton 
                          edge='end' 
                          size="small" 
                          disableRipple
                          data-testid="restaurant-group-clear-btn"
                          onClick={handleRestaurantGroupClear}
                        >
                          <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                  {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                    value="All"
                  >
                    {filters?.restaurant_group_id === ALL_VALUE ? (
                      <SelectedMenuItem data={'All'} />
                    ) : (
                      'All'
                    )}
                  </StyledMenuItem>
                  )}
                  {
                    restaurantGroups
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.label}
                          value={res?.label}
                        >
                          {hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value}
                        </StyledMenuItem>
                    ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl 
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "200px" }
                }}
              >
                <StyledInputLabel>Restaurant</StyledInputLabel>
                <Select
                  multiple
                  label="Restaurant"
                  value={hasSingleAvailableRestaurant
                    ? [getSingleItemValue(restaurants)?.name]
                    : (filters?.restaurants || [ALL_VALUE])}
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  renderValue={() =>
                    hasSingleAvailableRestaurant
                      ? (getSingleItemValue(restaurants)?.name || 'All')
                      : getRestaurantDisplayValue()
                  }
                  sx={{ 
                    ...selectBaseStyles, 
                    minWidth: { xs: "100%", sm: "200px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleMultiSelectChange}
                  onClose={() => setTimeout(() => setRestaurantSearch(""), 1000)}
                  endAdornment= {
                    !filters?.restaurants?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton 
                        edge='end' 
                        size="small" 
                        disableRipple
                        data-testid="restaurant-clear-btn"
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />

                  {!hasSingleAvailableRestaurant && (
                    <StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.restaurants?.includes(ALL_VALUE)}
                    >
                      {filters?.restaurants?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                  )}

                  {restaurants
                    ?.map((res) => (
                      <StyledMenuItem
                        key={res?.id}
                        value={res?.name}
                      >
                        {
                          hasSingleAvailableRestaurant || filters?.restaurants?.includes(res?.name) ? (
                            <SelectedMenuItem data={res?.name} />
                          ) : (
                            <>
                              {res?.name}
                            </>
                          )
                        }
                      </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl 
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Date Range</StyledInputLabel>
                <Select
                  value={filters?.dateFilter || ALL_VALUE}
                  renderValue={() => 
                    filters?.dateFilter
                  }
                  label="Date Range"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: '250px' } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={(e) =>
                    handleFilterChange("dateFilter", e.target.value)
                  }
                  endAdornment={
                    filters?.dateFilter !== ALL_VALUE? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          data-testid="date-range-clear-btn"
                          onClick={handleDateRangeClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {/* All option */}
                  <StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  {Days?.map((value) => (
                    <StyledMenuItem
                      key={value}
                      value={value}
                    >
                      {
                        filters?.dateFilter === value ? <SelectedMenuItem data={value} /> : value
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              {filters.dateFilter === "Custom Date" && (
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    width: { xs: "100%", sm: "auto" }
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      Days
                    </Typography>
                    <DatePicker
                      format="MM-DD-YYYY"
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: '160px' }
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateFrom || null}
                      onChange={(date) =>
                        handleFilterChange("customDateFrom", date)
                      }
                    />
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      through
                    </Typography>
                    <DatePicker
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: '160px' }
                      }}
                      format="MM-DD-YYYY"
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateTo || null}
                      onChange={(date) =>
                        handleFilterChange("customDateTo", date)
                      }
                    />
                  </LocalizationProvider>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                status={isFetching}
              /> 

              <UpdateButton
                onClick={handleUpdate}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading report={false}>Restaurant Perq Report</MenuHeading>

        {isFetching ? (
          <Loader />
        ) : (
          <>
            <StyledPerPageContainer>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Select
                  value={entriesPerPage}
                  size="small"
                  sx={perPageStyles}
                  onChange={(e) => setEntriesPerPage(e.target.value)}
                >
                  {[10, 25, 50, 100].map((num) => (
                    <StyledMenuItem
                      key={num}
                      value={num}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {num}
                    </StyledMenuItem>
                  ))}
                </Select>
                <TableDetailText
                  variant="body2"
                >
                  entries per page
                </TableDetailText>
              </div>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <TableDetailText
                  variant="body2"
                >
                  Showing {totalItems === 0 ? 0 : startIndex + 1} to{" "}
                  {Math.min(endIndex, totalItems)} of {totalItems} report
                  entries
                </TableDetailText>
                {
                  csvLoading ? (
                    <IconButton disableRipple data-testid="restaurant-discount-export-loading">
                      <img style={{ height: 24 }} src={csvLoader} alt="loading..." />
                    </IconButton>
                  ) : (
                    <IconButton
                      data-testid="restaurant-discount-export-btn"
                      onClick={handleOrderCsvExport}
                      disabled={filteredData?.length === 0 || csvLoading}
                      sx={{color: '#2b4fb9'}}
                    >
                      <DownloadIcon />
                    </IconButton>
                  )
                }
              </Box>
            </StyledPerPageContainer>

            <Box sx={{ position: "relative" }}>
              <StyledTableContainer
                ref={tableRef}
                onScroll={handleMainScroll}
                sx={{
                  mt: 1,
                  boxShadow: "none",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: containerMaxHeight,
                }}
              >
                <Table>
                  <StyledTableHead report={false}>
                    <TableRow>
                      {columns.map((col, index) => (
                        <StyledTableHeadCell key={index} sx={{ width: columnWidths[normalizeKey(col?.key)], minWidth: 80, position: "relative", userSelect: "none" }}>
                          <TableSortLabel
                            active={orderBy === col.key}
                            direction={orderBy === col.key ? order : "asc"}
                            onClick={() => handleSort(col)}
                          >
                            {col.label}
                          </TableSortLabel>
                          <Box
                            onMouseDown={(e) => startResizing(col.key, e)}
                            sx={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              height: "100%",
                              width: "6px",
                              cursor: "col-resize",
                              zIndex: 10,
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
                            }}
                          />
                        </StyledTableHeadCell>
                      ))}
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {visibleRows?.length === 0 ? (
                      <StyledTableBodyCell colSpan={columns.length}>
                        <StyledTypography variant="body2">
                          {isDiscountCardsPath
                            ? t("discount-cards.index.table.no-data-exists")
                            : "No Data Exists"}
                        </StyledTypography>
                      </StyledTableBodyCell>
                    ) : (
                      <>
                        <StyledTableRow report={false}>
                          <StyledTableBodyCell
                            report={false}
                            sx={{ color: "rgba(34, 71, 172, 1)" }}
                          >
                            Total Perqs
                          </StyledTableBodyCell>
                          {columns?.slice(1).map((col, index) => {
                            const columnTotal = (filteredData || []).reduce((sum, item) => {
                              const value = item?.[col.key];
                              return sum + (typeof value === "number" ? value : 0);
                            }, 0);
                            return (
                              <StyledTableBodyCell
                                report={false}
                                key={index}
                                sx={{ color: "rgba(34, 71, 172, 1)" }}
                              >
                                {`$${columnTotal.toFixed(2)}`}
                              </StyledTableBodyCell>
                            );
                          })}
                        </StyledTableRow>
                        {visibleRows?.slice(startIndex, endIndex).map((item, index) => (
                          <StyledTableRow report={false} key={index}>
                            {columns.map((col, index) => (
                              <StyledTableBodyCell report={false} key={index} sx={{width: columnWidths[col?.key], minWidth: 80}}>
                                {item[col.key]}
                              </StyledTableBodyCell>
                            ))}
                          </StyledTableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>

              <Box ref={tableFooterRef} sx={{ display: 'flow-root' }}>
                <Box
                  ref={fakeScrollRef}
                  onScroll={handleFakeScroll}
                  sx={scrollbarStyles}
                >
                  <Box
                    sx={{
                      width: scrollWidth || '100%',
                      height: "1px",
                    }}
                  />
                </Box>

                <StyledPaginationContainer>
                  <Pagination
                    page={currentPage}
                    count={totalPages}
                    onChange={(_event, page) => setCurrentPage(page)}
                  />
                </StyledPaginationContainer>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </React.Fragment>
  );
};
